import {
  ExpiryType,
  InventoryManagementMethod,
  isDefined,
  isLimitOrderType,
  isListedInstrumentTradableInApp,
  isStopActivationOrderType,
  ListedInstrument,
  OrderExchangeData,
  OrderType,
  TradeType,
  TradingSession,
} from '@eih-netbroker/common';
import { useCallback, useEffect, useEffectEvent, useRef } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { TradeTypeSwitch2 } from '../../../../../shared/components/switch/TradeTypeSwitch2';
import { Config } from '../../../../../shared/config/config';
import { useDefaultOrderPrices } from '../../../services/order/useDefaultOrderPrices';
import { useOrderExpiryType } from '../../../services/order/useOrderExpiryType';
import { useOrderTradingSession } from '../../../services/order/useOrderTradingSession';
import { useSuggestedPrices } from '../../../services/order/useSuggestedPrices';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import { OrderCurrencyExchange } from '../order-currency-exchange/OrderCurrencyExchange';
import { OrderTransactionInfo } from '../order-transaction-info/OrderTransactionInfo';
import classes from './OrderForm2.module.scss';
import { OrderFormAdditionalSettings } from './OrderFormAdditionalSettings';
import { OrderFormInstrumentSearch } from './OrderFormInstrumentSearch';
import { OrderFormPriceInputs } from './OrderFormPriceInputs';
import { OrderFormQuantityInput } from './OrderFormQuantityInput';
import { OrderFormTypeSelector } from './OrderFormTypeSelector';

export type OrderCurrencyExchangeParams = {
  sourceAmount?: number;
  targetAmount: number;
  exchangeRate?: number;
  sourceCurrency: string;
};

export type OrderFormData = {
  instrument?: ListedInstrument | null;
  orderType?: OrderType;
  tradeType?: TradeType;
  quantity?: number;
  price?: number;
  stopActivationPrice?: number;
  expiryType?: ExpiryType;
  expiry?: Date;
  inventoryManagementMethod?: InventoryManagementMethod | null;
  tradingSession?: TradingSession;
  currencyExchangeParams?: 'initialized' | OrderCurrencyExchangeParams;
};

export type PriceValidationState = {
  isLoading?: boolean;
  errorMessage?: string;
  warningMessage?: string;
};

interface OrderFormProps {
  order: OrderFormData;
  exchangeData?: OrderExchangeData;
  widgetGroupId?: string | number;
  onChange: (order: OrderFormData) => void;
  onInstrumentChange: (instrument: ListedInstrument | null) => void;
  onStopActivationPriceRevalidate: (order: OrderFormData) => Promise<void>;
  priceValidationState?: PriceValidationState;
  stopActivationPriceValidationState?: PriceValidationState;
  isModify?: boolean;
  transactionId?: string;
  orderRequestLoading: boolean;
  isOrderFormDetailsExpanded?: boolean;
  sourceComponent?: OrderFormSourceComponent;
  shouldUseCountdownBarPlaceholder?: boolean;
}

export const OrderForm2 = ({
  order,
  exchangeData,
  onChange,
  onInstrumentChange,
  onStopActivationPriceRevalidate,
  priceValidationState,
  stopActivationPriceValidationState,
  isModify,
  transactionId,
  orderRequestLoading,
  sourceComponent = OrderFormSourceComponent.Modal,
  shouldUseCountdownBarPlaceholder = false,
}: OrderFormProps) => {
  const { translation } = useLocale();
  const { getInitialTradingSession } = useOrderTradingSession();
  const { getDefaultExpiryType, getDefaultExpiryDate } = useOrderExpiryType();
  const tradeable = isListedInstrumentTradableInApp(order.instrument);
  const instrumentId = order.instrument?.ric;
  const suggestedPrices = useSuggestedPrices(order.instrument);

  const { defaultLimitPrice, defaultActivationPrice, areDefaultPricesInitializing, refreshDefaultPrices } =
    useDefaultOrderPrices(order);
  const lastGtdExpiryRef = useRef<Date | undefined>(undefined);
  const defaultPriceInstrumentIdRef = useRef(instrumentId);
  const orderRef = useRef(order);
  orderRef.current = order;

  const handleInstrumentChange = useCallback(
    (instrument: ListedInstrument | null) => {
      if (exchangeData?.isEnabled) {
        exchangeData.setEnabled(false);
      }
      onInstrumentChange(instrument);
    },
    [onInstrumentChange, exchangeData],
  );

  const handleChange = useCallback(
    <T extends keyof OrderFormData>(key: T, value: OrderFormData[T]) => {
      const current = orderRef.current;
      if (key === 'orderType') {
        const nextOrderType = value as OrderType;
        const expiryType = getDefaultExpiryType(current.instrument, nextOrderType);
        const expiry =
          expiryType === ExpiryType.Gtd
            ? lastGtdExpiryRef.current ?? getDefaultExpiryDate(current.instrument, expiryType)
            : undefined;
        const tradingSession = getInitialTradingSession(current.instrument, nextOrderType);

        onChange({
          ...current,
          tradingSession,
          expiryType,
          expiry,
          [key]: value,
          price: undefined,
          stopActivationPrice: undefined,
        });
      } else if (key === 'expiryType') {
        const expiry =
          value === ExpiryType.Gtd
            ? lastGtdExpiryRef.current ?? getDefaultExpiryDate(current.instrument, value)
            : undefined;

        onChange({
          ...current,
          [key]: value,
          expiry,
        });
      } else if (key === 'expiry') {
        lastGtdExpiryRef.current = value as Date | undefined;
        onChange({ ...current, [key]: value });
      } else if (key === 'tradeType') {
        onChange({
          ...current,
          [key]: value,
          price: undefined,
          stopActivationPrice: undefined,
        });
      } else if (key === 'price' || key === 'stopActivationPrice' || key === 'quantity') {
        const effectiveValue = isDefined(value) ? value : 0;
        onChange({
          ...current,
          [key]: effectiveValue,
        });
      } else {
        onChange({ ...current, [key]: value });
      }
    },
    [onChange, getDefaultExpiryType, getDefaultExpiryDate, getInitialTradingSession],
  );

  const initDefaultPrices = useEffectEvent(() => {
    refreshDefaultPrices(order.instrument ?? null);
  });

  const setDefaultPriceForInput = useEffectEvent(() => {
    if (!isDefined(order.instrument)) {
      defaultPriceInstrumentIdRef.current = undefined;
      refreshDefaultPrices(null);
      return;
    }

    if (areDefaultPricesInitializing) {
      return;
    }

    let hasChanged = false;
    const copyForm = { ...order };
    const instrumentChanged = defaultPriceInstrumentIdRef.current !== instrumentId;

    if (isLimitOrderType(order.orderType) && (instrumentChanged || !isDefined(order.price))) {
      copyForm.price = defaultLimitPrice ?? 0;
      hasChanged = true;
    }
    if (isStopActivationOrderType(order.orderType) && (instrumentChanged || !isDefined(order.stopActivationPrice))) {
      copyForm.stopActivationPrice = defaultActivationPrice ?? 0;
      hasChanged = true;
    }

    defaultPriceInstrumentIdRef.current = instrumentId;

    if (hasChanged) {
      onChange(copyForm);
    }
  });

  const revalidateStopActivationPrice = useEffectEvent(() => {
    if (isStopActivationOrderType(order.orderType) && isDefined(order.stopActivationPrice)) {
      onStopActivationPriceRevalidate(order);
    }
  });

  useEffect(() => {
    setDefaultPriceForInput();
    // transactionId is required when the form is initiated from the widget,
    // and the confirm/2FA modal had been closed with resetFormToDefaults = true (=> and generates new transactionId)
  }, [
    instrumentId,
    defaultLimitPrice,
    defaultActivationPrice,
    order.orderType,
    order.tradeType,
    areDefaultPricesInitializing,
    transactionId,
  ]);

  useEffect(() => {
    initDefaultPrices();
  }, [instrumentId]);

  useEffect(() => {
    revalidateStopActivationPrice();
  }, [suggestedPrices.lastPrice]);

  return (
    <div className={classes.container}>
      <OrderFormInstrumentSearch
        instrument={order.instrument ?? null}
        updateInstrument={handleInstrumentChange}
        source={transactionId ?? 'order-form2'}
        transactionId={transactionId}
        disabled={isModify}
        readOnly={orderRequestLoading}
      />
      <div className={classes.dividerWide} />
      <OrderTransactionInfo
        order={order}
        sourceComponent={sourceComponent}
      />
      <TradeTypeSwitch2
        selectedValue={order.tradeType}
        onChange={value => handleChange('tradeType', value)}
        disabled={isModify || !isDefined(order.instrument)}
        readOnly={orderRequestLoading}
      />
      <OrderFormTypeSelector
        instrument={order.instrument}
        selectedValue={order.orderType}
        onChange={value => handleChange('orderType', value)}
        disabled={!isDefined(order.instrument) || tradeable === false || isModify}
        readOnly={orderRequestLoading}
      />
      <OrderFormQuantityInput
        label={translation.orderModal.quantity}
        order={order}
        handleChange={handleChange}
        suffix={translation.orderModal.quantityEndorment}
        disabled={!isDefined(order.instrument) || tradeable === false}
        readOnly={orderRequestLoading}
        testId="order-form-quantity-input"
      />
      {isDefined(order.instrument) && (
        <OrderFormPriceInputs
          order={order}
          onChange={handleChange}
          readOnly={orderRequestLoading}
          isLoading={areDefaultPricesInitializing}
          disabled={tradeable === false}
          suggestedPrices={suggestedPrices}
          priceValidationState={priceValidationState}
          stopActivationPriceValidationState={stopActivationPriceValidationState}
        />
      )}
      {Config.FeatureFlags.isOrderCurrencyExchangeEnabled && (
        <>
          <div className={classes.divider} />
          <OrderCurrencyExchange
            disabled={!isDefined(order.instrument)}
            exchangeData={exchangeData}
            orderRequestLoading={orderRequestLoading}
            shouldUseCountdownBarPlaceholder={shouldUseCountdownBarPlaceholder}
            sourceComponent={sourceComponent}
          />
        </>
      )}
      <div className={classes.divider} />
      <OrderFormAdditionalSettings
        order={order}
        onChange={handleChange}
        isLoading={orderRequestLoading}
        disabled={!isDefined(order.instrument) || tradeable === false}
      />
    </div>
  );
};
